import { CSSProperties, RefObject, useEffect, useRef, useState } from "react";
import "../../styles/guided-tutorial.css";

type GuidedStep = {
  targetRef: RefObject<HTMLElement>;
  title: string;
  description: string;
  elevateParentSelectors?: string[];
};

type GuidedTutorialProps = {
  storageKey: string;
  enabled: boolean;
  steps: GuidedStep[];
  nextSystemStep?: string | null;
  clearReplayOnFinish?: boolean;
  markTutorialCompleted?: boolean;
  lockInteractionToTarget?: boolean;
};

const TUTORIAL_SESSION_STEP_OVERRIDE_KEY = "management_tutorial_session_step_override";

export default function GuidedTutorial({
  storageKey,
  enabled,
  steps,
  nextSystemStep,
  clearReplayOnFinish = false,
  markTutorialCompleted = false,
  lockInteractionToTarget = false,
}: GuidedTutorialProps) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>({});
  const tooltipRef = useRef<HTMLDivElement>(null);

  const step = steps[index];
  const targetElement = step?.targetRef?.current ?? null;
  const elevateParentSelectors = step?.elevateParentSelectors;
  const elevatedSelectorKey =
    elevateParentSelectors === undefined
      ? "__default__"
      : elevateParentSelectors.join("|");

  const finish = (dismissed = false) => {
    window.localStorage.setItem(storageKey, "1");
    if (nextSystemStep) {
      window.localStorage.setItem("management_tutorial_step", nextSystemStep);
    }
    if (markTutorialCompleted) {
      window.localStorage.setItem("management_tutorial_completed", "1");
    }
    if (clearReplayOnFinish) {
      window.localStorage.removeItem("management_tutorial_replay");
      window.localStorage.setItem("management_tutorial_agenda_seen", "1");
      window.localStorage.removeItem("management_tutorial_step");
    }
    if (dismissed && nextSystemStep) {
      window.sessionStorage.setItem(TUTORIAL_SESSION_STEP_OVERRIDE_KEY, nextSystemStep);
      window.dispatchEvent(new Event("management:tutorial-progressed"));
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!enabled || !steps.length) return;
    const done = window.localStorage.getItem(storageKey) === "1";
    if (!done) {
      setIndex(0);
      setOpen(true);
    }
  }, [enabled, storageKey, steps.length]);

  useEffect(() => {
    if (!lockInteractionToTarget) return;

    if (open) {
      document.body.classList.add("guided-lock-active");
    } else {
      document.body.classList.remove("guided-lock-active");
    }

    return () => {
      document.body.classList.remove("guided-lock-active");
    };
  }, [lockInteractionToTarget, open]);

  useEffect(() => {
    if (!lockInteractionToTarget || !open || !targetElement) return;

    const tooltipEl = tooltipRef.current;
    const focusableSelector = [
      "button",
      "input",
      "select",
      "textarea",
      "a[href]",
      "[tabindex]",
      "[contenteditable='true']",
    ].join(",");

    const changedElements: HTMLElement[] = [];
    const allFocusable = Array.from(document.querySelectorAll<HTMLElement>(focusableSelector));

    allFocusable.forEach((element) => {
      const isInsideTarget = targetElement.contains(element) || element === targetElement;
      const isInsideTooltip = tooltipEl ? tooltipEl.contains(element) || element === tooltipEl : false;

      if (isInsideTarget || isInsideTooltip) return;

      changedElements.push(element);

      element.dataset.guidedPrevTabindex = element.getAttribute("tabindex") ?? "";
      element.setAttribute("tabindex", "-1");
      element.setAttribute("aria-hidden", "true");
    });

    return () => {
      changedElements.forEach((element) => {
        const prevTabindex = element.dataset.guidedPrevTabindex ?? "";
        if (prevTabindex === "") element.removeAttribute("tabindex");
        else element.setAttribute("tabindex", prevTabindex);
        element.removeAttribute("aria-hidden");
        delete element.dataset.guidedPrevTabindex;
      });
    };
  }, [lockInteractionToTarget, open, targetElement, index]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || !targetElement) return;

    const el = targetElement;
    const elevatedParents =
      elevateParentSelectors === undefined
        ? Array.from(
            new Set(
              [
                el.closest(".form-check"),
                el.closest(".notification-card"),
              ].filter(Boolean) as HTMLElement[]
            )
          )
        : Array.from(
            new Set(
              elevateParentSelectors
                .map((selector) => el.closest(selector))
                .filter(Boolean) as HTMLElement[]
            )
          );
    el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    if (
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLSelectElement
    ) {
      const shouldAutoFocus = !(el instanceof HTMLInputElement && el.type === "date");
      if (shouldAutoFocus) {
        window.requestAnimationFrame(() => el.focus());
      }
    }

    const updateRect = () => setRect(el.getBoundingClientRect());
    const raf = window.requestAnimationFrame(updateRect);
    updateRect();

    el.classList.add("guided-highlight-target");
    elevatedParents.forEach((parent) => parent.classList.add("guided-highlight-parent"));
    const onResize = () => updateRect();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);

    return () => {
      window.cancelAnimationFrame(raf);
      el.classList.remove("guided-highlight-target");
      elevatedParents.forEach((parent) => parent.classList.remove("guided-highlight-parent"));
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [index, open, targetElement, elevatedSelectorKey]);

  useEffect(() => {
    if (!open || !rect) return;

    const viewportPadding = 16;
    const gap = 20;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tooltipWidth = tooltipRef.current?.offsetWidth ?? 560;
    const tooltipHeight = tooltipRef.current?.offsetHeight ?? 180;
    const isMobile = vw <= 768;
    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

    if (isMobile) {
      setTooltipStyle({
        top: viewportPadding,
        left: clamp((vw - tooltipWidth) / 2, viewportPadding, Math.max(viewportPadding, vw - tooltipWidth - viewportPadding)),
      });
      return;
    }

    const centeredTop = clamp(
      rect.top + rect.height / 2 - tooltipHeight / 2,
      viewportPadding,
      Math.max(viewportPadding, vh - tooltipHeight - viewportPadding)
    );
    const fitsRight = vw - rect.right >= tooltipWidth + gap + viewportPadding;
    const fitsLeft = rect.left >= tooltipWidth + gap + viewportPadding;
    const centeredLeft = clamp(
      rect.left + rect.width / 2 - tooltipWidth / 2,
      viewportPadding,
      Math.max(viewportPadding, vw - tooltipWidth - viewportPadding)
    );
    const fitsAbove = rect.top >= tooltipHeight + gap + viewportPadding;
    const fitsBelow = vh - rect.bottom >= tooltipHeight + gap + viewportPadding;

    if (fitsRight) {
      setTooltipStyle({
        top: centeredTop,
        left: rect.right + gap,
      });
      return;
    }

    if (fitsLeft) {
      setTooltipStyle({
        top: centeredTop,
        left: rect.left - tooltipWidth - gap,
      });
      return;
    }

    if (fitsAbove) {
      setTooltipStyle({
        top: rect.top - tooltipHeight - gap,
        left: centeredLeft,
      });
      return;
    }

    if (fitsBelow) {
      setTooltipStyle({
        top: rect.bottom + gap,
        left: centeredLeft,
      });
      return;
    }

    setTooltipStyle({
      top: viewportPadding,
      left: centeredLeft,
    });
  }, [open, rect, index]);

  if (!open || !step) return null;

  return (
    <>
      <div className="guided-overlay-pane" style={{ inset: 0 }} />
      <div ref={tooltipRef} className="guided-tooltip" style={tooltipStyle}>
        <span className="guided-step">Passo {index + 1} de {steps.length}</span>
        {step.title ? <h6>{step.title}</h6> : null}
        <p>{step.description}</p>
        <div className="guided-actions">
          <button type="button" className="guided-skip" onClick={() => finish(true)}>Pular tutorial</button>
          <div className="guided-nav">
            <button type="button" onClick={() => setIndex((prev) => Math.max(0, prev - 1))} disabled={index === 0}>
              Voltar
            </button>
            <button
              type="button"
              className="guided-next"
              onClick={() => (index === steps.length - 1 ? finish() : setIndex((prev) => prev + 1))}
            >
              {index === steps.length - 1 ? "Finalizar" : "Próximo"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
